// TypeScript interfaces for the Micro-Learning feature
// These interfaces match the Prisma schema models

export enum LearningDifficulty {
  BEGINNER = 'BEGINNER',
  INTERMEDIATE = 'INTERMEDIATE',
  ADVANCED = 'ADVANCED'
}

export enum ModuleStepType {
  CONTENT = 'CONTENT',
  INTERACTIVE = 'INTERACTIVE',
  QUIZ = 'QUIZ',
  REFLECTION = 'REFLECTION',
  SUMMARY = 'SUMMARY',
  RESOURCES = 'RESOURCES'
}

export enum LearningContentType {
  CONCEPT_INTRO = 'CONCEPT_INTRO',
  CONCEPT_BREAKDOWN = 'CONCEPT_BREAKDOWN',
  EXAMPLE_ANALYSIS = 'EXAMPLE_ANALYSIS',
  CASE_STUDY = 'CASE_STUDY',
  STEP_BY_STEP = 'STEP_BY_STEP',
  COMPARISON = 'COMPARISON',
  TIMELINE = 'TIMELINE',
  INFOGRAPHIC = 'INFOGRAPHIC'
}

export enum InteractiveType {
  GUIDED_ANALYSIS = 'GUIDED_ANALYSIS',
  APPLIED_PRACTICE = 'APPLIED_PRACTICE',
  SCENARIO_BASED = 'SCENARIO_BASED',
  DRAG_DROP = 'DRAG_DROP',
  FILL_BLANK = 'FILL_BLANK',
  SORTING = 'SORTING',
  MATCHING = 'MATCHING',
  HOTSPOT = 'HOTSPOT',
  SLIDER_SCALE = 'SLIDER_SCALE',
  TEXT_INPUT = 'TEXT_INPUT',
  SIMULATION = 'SIMULATION'
}

export enum AssessmentType {
  MULTIPLE_CHOICE = 'MULTIPLE_CHOICE',
  TRUE_FALSE = 'TRUE_FALSE',
  MATCHING = 'MATCHING',
  ORDERING = 'ORDERING',
  SHORT_ANSWER = 'SHORT_ANSWER',
  LONG_ANSWER = 'LONG_ANSWER',
  FILL_IN_BLANKS = 'FILL_IN_BLANKS',
  DRAG_AND_DROP = 'DRAG_AND_DROP',
  HOTSPOT_CLICK = 'HOTSPOT_CLICK',
  SCALE_RATING = 'SCALE_RATING',
  CHECKLIST = 'CHECKLIST'
}

export enum ModuleStatus {
  DRAFT = 'DRAFT',
  REVIEW = 'REVIEW',
  PUBLISHED = 'PUBLISHED',
  ARCHIVED = 'ARCHIVED'
}

export enum UserModuleStatus {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  SKIPPED = 'SKIPPED'
}

export enum FeedbackType {
  RATING = 'RATING',
  ACCURACY_FLAG = 'ACCURACY_FLAG',
  USEFULNESS_FLAG = 'USEFULNESS_FLAG',
  DIFFICULTY_FLAG = 'DIFFICULTY_FLAG',
  CONTENT_SUGGESTION = 'CONTENT_SUGGESTION'
}

// Hybrid content format - supports both markdown and structured content
export interface HybridContent {
  type: 'markdown' | 'structured';
  data: string | any; // string for markdown, object for structured
}

// Legacy content structure interfaces (for backward compatibility)
export interface ConceptIntroContent {
  definition: string;
  relevance: string;
  components?: {
    title: string;
    points: string[];
  }[];
}

export interface ConceptBreakdownContent {
  introduction: string;
  elements: {
    term: string;
    definition: string;
    examples?: (string | Record<string, any>)[];
    example?: string;
    icon?: string;
  }[];
}

export interface GuidedAnalysisExercise {
  content: string;
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

export interface AppliedPracticeScenario {
  event: string;
  sources?: {
    outlet: string;
    headline: string;
    excerpt: string;
  }[];
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

export interface QuizQuestion {
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

// Main interfaces matching database models
export interface LearningModule {
  id: string;
  title: string;
  description: string;
  category: string;
  difficulty: LearningDifficulty;
  estimatedTime: string;

  // Content organization
  keyTakeaways: string[];
  relatedTopics: string[];
  prerequisites: string[];

  // Series and progression (required)
  seriesId: string;
  seriesOrder: number;
  seriesTitle: string;
  series?: LearningSeries;

  // Versioning and quality control
  version: number;
  status: ModuleStatus;

  // Analytics and performance
  completionCount: number;
  averageRating?: number;
  averageTime?: number; // in seconds

  // Rewards
  seedReward: number;

  // Content management
  createdBy?: string;
  reviewedBy?: string;
  publishedAt?: Date;

  createdAt: Date;
  updatedAt: Date;

  // Relations
  steps: ModuleStep[];
  userProgress?: UserModuleProgress[];
  feedback?: ModuleFeedback[];
  versions?: ModuleVersion[];
}

export interface LearningSeries {
  id: string;
  title: string;
  description: string;
  category: string;
  difficulty: LearningDifficulty;

  // Metadata
  totalModules: number;
  estimatedTotalTime?: string;

  createdAt: Date;
  updatedAt: Date;

  // Relations
  modules: LearningModule[];
  userSeriesProgress?: UserSeriesProgress[];
}

export interface ModuleStep {
  id: string;
  moduleId: string;
  module?: LearningModule;

  stepOrder: number;
  stepType: ModuleStepType;
  title: string;

  // Content configuration
  contentType?: LearningContentType;
  interactiveType?: InteractiveType;

  // Flexible content storage - supports both hybrid and legacy formats
  content:
    | HybridContent
    | ConceptIntroContent
    | ConceptBreakdownContent
    | {
        introduction?: string;
        exercises?: GuidedAnalysisExercise[];
        scenario?: AppliedPracticeScenario;
        questions?: QuizQuestion[];
        [key: string]: any; // Allow for future content types
      };

  // Media and visual elements
  visualCue?: string;
  mediaUrl?: string;
  mediaType?: string;

  createdAt: Date;
  updatedAt: Date;

  // Relations
  assessments?: StepAssessment[];
  userStepProgress?: UserStepProgress[];
}

export interface StepAssessment {
  id: string;
  stepId: string;
  step?: ModuleStep;

  questionOrder: number;
  assessmentType: AssessmentType;

  // Question content
  question: string;
  options: string[];
  correctAnswer: any; // Flexible for different answer types
  explanation?: string;

  // Difficulty and analytics
  difficulty: number; // 1-5 scale
  successRate?: number;

  createdAt: Date;
  updatedAt: Date;

  // Relations
  userAnswers?: UserAssessmentAnswer[];
}

export interface UserModuleProgress {
  id: string;
  userId: string;
  moduleId: string;

  user?: any; // User type from your existing schema
  module?: LearningModule;

  status: UserModuleStatus;

  // Performance metrics
  score?: number;
  timeSpent: number; // in seconds
  attemptsCount: number;

  // Restart tracking
  isRestarted: boolean;
  restartCount: number;
  originalCompletedAt?: Date; // First completion
  lastRestartedAt?: Date;

  // Timestamps
  startedAt?: Date;
  completedAt?: Date;
  lastAccessedAt?: Date;

  createdAt: Date;
  updatedAt: Date;

  // Series progress information (calculated dynamically)
  currentModuleNumber?: number;
  totalModulesInSeries?: number;
  completedModulesInSeries?: number;
}

export interface UserStepProgress {
  id: string;
  userId: string;
  stepId: string;
  moduleProgressId: string;

  user?: any;
  step?: ModuleStep;
  moduleProgress?: UserModuleProgress;

  isCompleted: boolean;
  timeSpent: number; // in seconds
  attemptsCount: number;

  completedAt?: Date;

  createdAt: Date;
  updatedAt: Date;
}

export interface UserSeriesProgress {
  id: string;
  userId: string;
  seriesId: string;

  user?: any;
  series?: LearningSeries;

  currentModuleId?: string;
  modulesCompleted: number;
  totalModules: number;

  // Restart tracking for series
  isRestarted: boolean;
  restartCount: number;
  originalCompletedAt?: Date;
  lastRestartedAt?: Date;

  startedAt?: Date;
  lastAccessedAt?: Date;

  createdAt: Date;
  updatedAt: Date;
}

export interface UserAssessmentAnswer {
  id: string;
  userId: string;
  assessmentId: string;

  user?: any;
  assessment?: StepAssessment;

  userAnswer: any; // Flexible for different answer types
  isCorrect: boolean;
  timeSpent: number; // in seconds

  answeredAt: Date;
}

export interface ModuleFeedback {
  id: string;
  userId: string;
  moduleId: string;

  user?: any;
  module?: LearningModule;

  feedbackType: FeedbackType;
  rating?: number; // 1-5 scale
  comment?: string;

  // Specific feedback flags
  isAccurate?: boolean;
  isUseful?: boolean;
  difficulty?: 'too_easy' | 'just_right' | 'too_hard';

  createdAt: Date;
}

export interface ModuleVersion {
  id: string;
  moduleId: string;
  module?: LearningModule;

  version: number;
  title: string;
  description: string;
  content: any; // Snapshot of entire module content

  // Change tracking
  changeReason?: string;
  changedBy?: string;

  createdAt: Date;
}

export interface UserLearningPreference {
  id: string;
  userId: string;

  user?: any;

  // Topic preferences
  preferredTopics: string[];
  excludedTopics: string[];

  // Difficulty and frequency preferences
  preferredDifficulty: LearningDifficulty[];
  maxModulesPerDay: number;

  // Learning style preferences
  preferInteractive: boolean;
  preferVisual: boolean;
  preferTextBased: boolean;

  createdAt: Date;
  updatedAt: Date;
}

// Utility types for API responses and component props
export interface ModuleCardProps {
  module: LearningModule;
  userProgress?: UserModuleProgress;
  onModuleClick?: (module: LearningModule) => void;
}

export interface ModuleModalProps {
  isOpen: boolean;
  onClose: () => void;
  module: LearningModule;
  userProgress?: UserModuleProgress;
}

export interface LearningFeedConfig {
  userId: string;
  preferences?: UserLearningPreference;
  maxModulesPerSession?: number;
  excludeCompletedModules?: boolean;
}

// API response types
export interface GetModulesResponse {
  modules: LearningModule[];
  totalCount: number;
  hasMore: boolean;
}

export interface ModuleProgressResponse {
  progress: UserModuleProgress;
  nextModule?: LearningModule;
  seriesProgress?: UserSeriesProgress;
}

export interface FeedbackSubmissionRequest {
  moduleId: string;
  feedbackType: FeedbackType;
  rating?: number;
  comment?: string;
  isAccurate?: boolean;
  isUseful?: boolean;
  difficulty?: 'too_easy' | 'just_right' | 'too_hard';
}

// Restart functionality interfaces
export interface RestartModuleRequest {
  moduleId: string;
  userId: string;
  reason?: string; // Optional reason for analytics
}

export interface RestartSeriesRequest {
  seriesId: string;
  userId: string;
  reason?: string;
}

export interface RestartResponse {
  success: boolean;
  newProgress: UserModuleProgress | UserSeriesProgress;
  message?: string;
}
